


/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */

import { faCheckCircle, faTimesCircle } from '@fortawesome/pro-duotone-svg-icons';
import { faInfoCircle } from '@fortawesome/pro-duotone-svg-icons/faInfoCircle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert, AlertTitle, Box, Button, Grid2, lighten, LinearProgress, Paper, styled, TextField, Typography } from '@mui/material';
import { ConditionMode, useScopedField, ValidateOn } from '@ngt/forms-core';
import {
    AuthenticatedUserContext,
    CollaboratingGroupContext,
    CrfCondition,
    CrfForm,
    DatePicker,
    FormBreadcrumbs,
    FormContext,
    FormErrorDisplayMode,
    FormLookupsContext,
    GroupedField,
    GroupedFields,
    ICrfConditionParameters,
    IFormContext,
    IFormLabel,
    InstitutionContext,
    MasterGroupContext,
    PageLayout,
    PatientContext,
    RouteLoading,
    Select,
    Text,
    ToggleButtonGroup
} from '@ngt/opms';
import { Permission, usePermissionsByIds } from '@ngt/opms-bctapi';
import React from 'react';

/*
 * ----------------------------------------------------------------------------------
 * Imports - Internal
 * ----------------------------------------------------------------------------------
 */
import * as Dtos from '../../../api/dtos';
import { IConsentForm } from '../../../api/dtos';
import { AppSettings } from '../../../AppSettings';
import EConsentContext, { EConsentUserType } from '../../../contexts/EConsentContext';
import useConsentVersionLookup, { PICF_VERSION_OTHER_ID } from '../../../hooks/lookups/useConsentVersionLookup';
import useInvestigatorLookup, { INVESTIGATOR_LOOKUP_OTHER } from '../../../hooks/lookups/useInvestigatorLookup';
import EConsentForm from '../EConsentForm';
import InputDisplay from '../inputs/InputDisplay';
import SelectWithTooltip from '../inputs/SelectWithTooltip';

/*
 * ---------------------------------------------------------------------------------
 * Functions
 * ---------------------------------------------------------------------------------
 */
const BoldParagraph = styled(Typography)(({theme}) => ({
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(1.5),
    fontWeight: 'bold',

    [theme.breakpoints.down('sm')]: {
        width: '80%'
    }
}))

const Paragraph = styled(Typography)(({theme}) => ({
    paddingBottom: theme.spacing(1.5),
}))

const StyledWrapper = styled(Box)(({ theme }) => ({
    padding: theme.spacing(0),

    [theme.breakpoints.up('md')]: {
        maxWidth: 1000,
        margin: 'auto',
        padding: theme.spacing(3),
    }
}))

const StyledHeader = styled(Typography)(({ theme }) => ({
    paddingBottom: theme.spacing(1.5),
    textAlign: 'center',
    fontSize: '1.25rem',
    color: theme.palette.primary.main,

    [theme.breakpoints.down('sm')]: {
        textAlign: 'left',
        width: '80%'
    }
}))

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3, 3, 3, 3),

    [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(5, 2, 5, 2),
        border: 'none',
        boxShadow: 'none'
    }
}))

const TrialInfo = styled(Alert)(({ theme }) => ({
    background: lighten(theme.palette.primary.main, 0.8),
    marginBottom: '1.5rem',
    padding: '1.5rem',
    
    '& *': {
        fontSize: '1rem'
    },
    '& > div': {
        padding: 0
    },
    '& > div > div': {
        marginBottom: '1rem'
    },
    '& > div > :last-of-type': {
        marginBottom: 0
    }
}))

const ChecklistContainer = styled(Box)(({ theme }) => ({
    marginLeft: '2rem',
    marginRight: '2rem',

    [theme.breakpoints.down('sm')]: {
        marginLeft: 0,
        marginRight: 0,
    }
}))

const Checklist = styled(Box)(() => ({
    display: 'flex'
}))

const ChecklistIcon = styled(Box)(({ theme }) => ({
    color: theme.palette.primary.light,
    marginRight: '1rem',
    fontSize: '1rem'
}))

const PicfButton = styled(Button)(({ theme }) => ({
    padding: theme.spacing(1)
}))


/*
 * ----------------------------------------------------------------------------------
 * Constants
 * ----------------------------------------------------------------------------------
 */

//const standardConsent = ({ formState }: ICrfConditionParameters<Dtos.MainConsent>) => formState?.value?.consentTypeId === Dtos.ConsentTypeEnum.Standard;
const patientSigned = ({ formState }: ICrfConditionParameters<Dtos.MainConsent>) => formState?.value?.patientSignedConsent === Dtos.LookupYesNoType.Yes;
const investigatorSigned = ({ formState }: ICrfConditionParameters<Dtos.MainConsent>) => formState?.value?.investigatorSignedConsent === Dtos.LookupYesNoType.Yes;
const picfVersionOther = ({ formState }: ICrfConditionParameters<Dtos.MainConsent>) => formState?.value?.picfId === PICF_VERSION_OTHER_ID;
const investigatorOther = ({ formState }: ICrfConditionParameters<Dtos.MainConsent>) => formState?.value?.investigator === INVESTIGATOR_LOOKUP_OTHER;

/*
 * ----------------------------------------------------------------------------------
 * Components
 * ----------------------------------------------------------------------------------
 */

interface IMainConsentProps { }

interface PicfVersionUpdaterProps {
    picfVersion: string | undefined;
}

const PicfVersionField: React.FunctionComponent = () => {
    const { data: patient } = React.useContext(PatientContext);

    const {
        state: {
            value: consentTypeId
        }
    } = useScopedField<Dtos.ConsentTypeEnum>(`consentTypeId`, { value: true }, false, false);
    
    const {data: picfLookup, loading: picfVersionsLoading } = useConsentVersionLookup('picfId', (patient as Dtos.Patient)?.institutionCode, 'Main')

    const [picfVersion, setPicfVersion] = React.useState<string|undefined>(undefined);

    const onPicfVersionChange = React.useCallback((picfId: number) => {
        const picfVersion = picfLookup?.items?.find(x => x.id === picfId)?.value;

        setPicfVersion(picfVersion);
    }, [picfLookup, setPicfVersion])

    const picfVersions = React.useMemo(() => {
        return [picfLookup];
    }, [picfLookup])

    const {
        state: {
            value: picfId
        }
    } = useScopedField<string>(`picfId`, { value: true }, false, false);

    const picfValue = React.useMemo(() => {
        return picfLookup?.items?.find(x => x.id == picfId)?.value
    }, [picfLookup, picfId]);

    return (
        <>
            <FormLookupsContext.Provider value={picfVersions}>
            {
                picfVersionsLoading ? 
                    <LinearProgress color='primary' /> : 
                    <GroupedFields>
                        <GroupedField
                            propertyPath="picfId"
                            component={SelectWithTooltip}
                            onChange={(event: any) => onPicfVersionChange(event.target.value)}
                            disabled={consentTypeId === Dtos.ConsentTypeEnum.Remote}
                            FieldGridProps={{
                                size: {
                                    xl: 3,
                                    lg: 3,
                                    md: 5,
                                    sm: 12,
                                    xs: 12
                                },
                            }}
                            tooltipProps={{
                                arrow: true,
                                placement: "right",
                                title: picfValue
                            }}
                        />
                        <CrfCondition
                            mode={ConditionMode.Show}
                            condition={picfVersionOther}
                            subscription={{ value: true }}
                        >
                            <GroupedField
                                propertyPath="picfVersionSpecify"
                                component={Text}
                            />
                        </CrfCondition>
                    </GroupedFields>
            }
            </FormLookupsContext.Provider>

            <PicfVersionUpdater picfVersion={picfVersion} />
        </>
    );
}

const PicfVersionUpdater: React.FunctionComponent<PicfVersionUpdaterProps> = ({
    picfVersion
}) => {
    const {
        context: {
            setValue: setPicfVersion
        }
    } = useScopedField<string>(`picfVersion`, { value: true }, false, false);

    React.useEffect(() => {
        if (picfVersion)
        {
            setPicfVersion(picfVersion);
        }
    }, [picfVersion, setPicfVersion]);

    return (
        null
    );
}

const EConsentNote: React.FunctionComponent = () => {
    const {
        state: {
            value: consentType
        }
    } = useScopedField<Dtos.ConsentTypeEnum>(`consentTypeId`, { value: true }, false, false);

    return (
        consentType === Dtos.ConsentTypeEnum.Remote ? 
            <Alert
                icon={<FontAwesomeIcon icon={faInfoCircle} fixedWidth />}
                severity="info"
            >
                <AlertTitle>
                    Note
                </AlertTitle>
                <Typography>
                    The rest of the form will be completed after the patient and the investigator have completed the eConsent.
                </Typography>
            </Alert>
            :
            null
    );
}

const PatientSigningUpdater: React.FunctionComponent = () => {
    const { userType: user } = React.useContext(EConsentContext);

    const {
        context: {
            setValue: setPatientSigning
        }
    } = useScopedField<boolean>(`patientSigning`, {value: true}, false, false);

    const {
        context: {
            setValue: setPatientSignedConsent
        }
    } = useScopedField<number>(`patientSignedConsent`, {value: true}, false, false);

    const {
        state: {
            value: patientSignedConsent
        }
    } = useScopedField<number>(`patientSignedConsent`, {value: true}, false, false);

    React.useEffect(() => {
        if(user === EConsentUserType.Participant && !patientSignedConsent)
        {
            setPatientSigning(true, true, true, true, false);
            setPatientSignedConsent(Dtos.LookupYesNoType.Yes, true, true, true, false);
        }

    }, [patientSignedConsent, setPatientSigning, setPatientSignedConsent, user]);

    return (
        null
    );
}

const InvestigatorSigningUpdater: React.FunctionComponent = () => {
    const { userType: user } = React.useContext(EConsentContext);

    const { data: authUser } = React.useContext(AuthenticatedUserContext);

    const {
        context: {
            setValue: setInvestigatorSigning
        }
    } = useScopedField<boolean>(`investigatorSigning`, {value: true}, false, false);

    const {
        context: {
            setValue: setInvestigatorSignedConsent
        }
    } = useScopedField<number>(`investigatorSignedConsent`, {value: true}, false, false);

    const {
        context: {
            setValue: setInvestigator
        }
    } = useScopedField<string>(`investigator`, {value: true}, false, false);

    const {
        state: {
            value: investigatorSignedConsent
        }
    } = useScopedField<number>(`investigatorSignedConsent`, {value: true}, false, false);

    React.useEffect(() => {
        if(user === EConsentUserType.Investigator && !investigatorSignedConsent)
        {
            setInvestigatorSigning(true, true, true, true, false);
            setInvestigatorSignedConsent(Dtos.LookupYesNoType.Yes, true, true, true, false);
            setInvestigator(authUser?.userName, true, true, true, false);
        }

    }, [investigatorSignedConsent, setInvestigatorSigning, setInvestigatorSignedConsent, setInvestigator, authUser, user]);

    return (
        null
    );
}

const consentLookups: Dtos.ILookup[] = [
    {
        propertyName: 'participantAgreedToProvideBloodSample',
        items: [
            {
                id: Dtos.LookupYesNoType.Yes,
                value: 'Yes'
            },
            {
                id: Dtos.LookupYesNoType.No,
                value: 'No'
            }
        ]
    },
    {
        propertyName: 'participantAgreedToCompleteQuestionnaires',
        items: [
            {
                id: Dtos.LookupYesNoType.Yes,
                value: 'Yes'
            },
            {
                id: Dtos.LookupYesNoType.No,
                value: 'No'
            }
        ]
    }
];


const consentLabels: IFormLabel[] = [
    {
        propertyPath: 'participantAgreedToProvideBloodSample',
        detailedLabel: undefined,
        label: 'I agree to provide blood samples for the trial.'
    },
    {
        propertyPath: 'participantAgreedToCompleteQuestionnaires',
        detailedLabel: undefined,
        label: 'I agree to take part in completing Questionnaires for the trial.'
    },
    {
        propertyPath: 'patientName',
        detailedLabel: undefined,
        label: 'Name'
    },
    {
        propertyPath: 'datePatientSignedConsent',
        detailedLabel: undefined,
        label: 'Date'
    },
    {
        propertyPath: 'investigatorName',
        detailedLabel: undefined,
        label: 'Name'
    },
    {
        propertyPath: 'dateInvestigatorSignedConsent',
        detailedLabel: undefined,
        label: 'Date'
    }
];

const MainConsentEConsentForm: React.FunctionComponent = () => {
    const { investigators, userType: user } = React.useContext(EConsentContext);
    const { data: consentForm } = React.useContext(FormContext) as unknown as IFormContext<IConsentForm>;

    const investigatorName = React.useMemo(() => {
        if(investigators && consentForm?.consentEvent?.investigator)
        {
            return investigators[consentForm.consentEvent.investigator]
        }

        return null;
    }, [investigators, consentForm]);

    return (
        <>
            <EConsentForm
                disableHeading
                disablePaper
                labels={consentLabels}
                lookups={consentLookups}
                validateOn={ValidateOn.onChange}
                debug={AppSettings.debug}
                hideSubmit
                submitAndReturnButtonText="Submit"
            >
                <PatientSigningUpdater />
                <InvestigatorSigningUpdater />
                <GroupedFields
                    ContainerProps={{
                        borderBottom: 'none !important',
                        borderTop: 'none !important'
                    }}
                >
                    <BoldParagraph>
                        Consent to take part in the optional parts of this trial 
                    </BoldParagraph>

                    <GroupedField
                        propertyPath="participantAgreedToProvideBloodSample"
                        component={ToggleButtonGroup}
                        FieldGridProps={{
                            size: {
                                xs: 12,
                                sm: 12,
                                md: 5,
                                lg: 4,
                                xl: 4
                            }
                        }}
                        ContainerGridProps={{
                            sx: {
                                paddingBottom: 1.5,
                                paddingTop: 1.5
                            }
                        }}
                        disabled={user === EConsentUserType.Investigator}
                    />

                    <GroupedField
                        propertyPath="participantAgreedToCompleteQuestionnaires"
                        component={ToggleButtonGroup}
                        FieldGridProps={{
                            size: {
                                xs: 12,
                                sm: 12,
                                md: 5,
                                lg: 4,
                                xl: 4
                            }
                        }}
                        ContainerGridProps={{
                            sx: {
                                paddingBottom: 1.5,
                                paddingTop: 1.5
                            }
                        }}
                        disabled={user === EConsentUserType.Investigator}
                    />
                    
                    <BoldParagraph>
                        Person taking part in the trial 
                    </BoldParagraph>
                    
                    <GroupedField
                        propertyPath="patientName"
                        component={InputDisplay}
                        valueConvert={(value: string, _lookups: any) => <TextField value={value?.toUpperCase()} disabled fullWidth/>}
                        FieldGridProps={{
                            size: {
                                xs: 12,
                                sm: 12,
                                md: 6,
                                lg: 6,
                                xl: 6
                            }
                        }}
                        ContainerGridProps={{
                            sx: {
                                paddingBottom: 1.5,
                                paddingTop: 1.5
                            }
                        }}
                    />

                    <GroupedField
                        propertyPath="datePatientSignedConsent"
                        component={DatePicker}
                        FieldGridProps={{
                            size: {
                                xs: 12,
                                sm: 12,
                                md: 6,
                                lg: 6,
                                xl: 6
                            }
                        }}
                        ContainerGridProps={{
                            sx: {
                                paddingBottom: 1.5,
                                paddingTop: 1.5
                            }
                        }}
                        disabled={user === EConsentUserType.Investigator}
                    />

                    {
                        user === EConsentUserType.Investigator && <>
                            <BoldParagraph>
                                Person conducting the informed consent discussion (Principal Investigator or Senior Researcher)
                            </BoldParagraph>

                            <ChecklistContainer>
                                <Checklist>
                                    <ChecklistIcon><FontAwesomeIcon fixedWidth icon={faCheckCircle} color="primary" /></ChecklistIcon>
                                    <Paragraph>
                                        I have explained the trial, its procedures and risks to the participant and I believe they have understood that explanation. 
                                    </Paragraph>
                                </Checklist>
                            </ChecklistContainer>

                            <GroupedField
                                propertyPath="investigatorName"
                                component={InputDisplay}
                                valueConvert={(_value: string, _lookups: any) => <TextField value={investigatorName?.toUpperCase()} disabled fullWidth/>}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 6,
                                        lg: 6,
                                        xl: 6
                                    }
                                }}
                                ContainerGridProps={{
                                    sx: {
                                        paddingBottom: 1.5,
                                        paddingTop: 1.5
                                    }
                                }}
                            />

                            <GroupedField
                                propertyPath="dateInvestigatorSignedConsent"
                                component={DatePicker}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 6,
                                        lg: 6,
                                        xl: 6
                                    }
                                }}
                                ContainerGridProps={{
                                    sx: {
                                        paddingBottom: 1.5,
                                        paddingTop: 1.5
                                    }
                                }}
                            />
                        </>
                    }
                </GroupedFields>
            </EConsentForm>
        </>
    )
}


const MainConsentEConsent: React.FunctionComponent = () => {

    const { userType, principalInvestigator } = React.useContext(EConsentContext);
    const { data: consentForm } = React.useContext(FormContext) as unknown as IFormContext<IConsentForm>;
    const { data: institution } = React.useContext(InstitutionContext);

    const mainPicfTypeId = 1;

    const picfsDownloadLink = `/opms/picf/download/${institution?.code}/${consentForm?.picfId}/${mainPicfTypeId}`;

    if(!consentForm || !userType || userType === EConsentUserType.Unknown) {
        return <>
            <StyledWrapper>
                <StyledPaper elevation={3}>
                    <Alert severity="error" icon={<FontAwesomeIcon fixedWidth icon={faTimesCircle} />} >
                        <AlertTitle>
                            Consent Form Not Loaded
                        </AlertTitle>
                        An error occured while loading the consent form. Please report the issue to your treatment team.
                    </Alert>
                </StyledPaper>
            </StyledWrapper>
        </>
    }

    if(userType === EConsentUserType.Investigator && !consentForm.patientSignedConsent) {
        return <>
            <StyledWrapper>
                <StyledPaper elevation={3}>
                    <Alert severity="error" icon={<FontAwesomeIcon fixedWidth icon={faTimesCircle} />} >
                        <AlertTitle>
                            Consent Form Not Available
                        </AlertTitle>
                        The consent form is still awaiting for participant's signature.
                    </Alert>
                </StyledPaper>
            </StyledWrapper>
        </>
    }

    return (
        <>
            <StyledWrapper>
                <StyledPaper elevation={3}>
                    <StyledHeader>
                        <strong>{consentForm?.formDefinition?.name?.toUpperCase()}</strong>
                    </StyledHeader>
                    <TrialInfo
                        icon={false}
                    >
                        <Grid2 container>
                                <Grid2
                                    size={{
                                        xs: 12,
                                        sm: 4,
                                        md: 3,
                                        lg: 3,
                                        xl: 3,
                                    }}
                                >
                                    <strong>Short Name of Trial</strong>
                                </Grid2>
                                <Grid2
                                    size={{
                                        xs: 12,
                                        sm: 8,
                                        md: 9,
                                        lg: 9,
                                        xl: 9,
                                    }}
                                >
                                    BCT 2401 PROSPECTIVE
                                </Grid2>
                        </Grid2>
                        <Grid2 container>
                                <Grid2
                                    size={{
                                        xs: 12,
                                        sm: 4,
                                        md: 3,
                                        lg: 3,
                                        xl: 3,
                                    }}
                                >
                                    <strong>Full Name of Trial </strong>
                                </Grid2>
                                <Grid2
                                    size={{
                                        xs: 12,
                                        sm: 8,
                                        md: 9,
                                        lg: 9,
                                        xl: 9,
                                    }}
                                >
                                    A two-arm, non-randomised, prospective, multicentre study using magnetic resonance imaging (MRI) findings and pathology features to select patients with early breast cancer for omission of post-operative radiotherapy 
                                </Grid2>
                        </Grid2>
                        <Grid2 container>
                                <Grid2
                                    size={{
                                        xs: 12,
                                        sm: 4,
                                        md: 3,
                                        lg: 3,
                                        xl: 3,
                                    }}
                                >
                                    <strong>Principal Investigator</strong>
                                </Grid2>
                                <Grid2
                                    size={{
                                        xs: 12,
                                        sm: 8,
                                        md: 9,
                                        lg: 9,
                                        xl: 9,
                                    }}
                                >
                                    {principalInvestigator}
                                </Grid2>
                        </Grid2>
                        <Grid2 container>
                                <Grid2
                                    size={{
                                        xs: 12,
                                        sm: 4,
                                        md: 3,
                                        lg: 3,
                                        xl: 3,
                                    }}
                                >
                                    <strong>Trial sponsor</strong>
                                </Grid2>
                                <Grid2
                                    size={{
                                        xs: 12,
                                        sm: 8,
                                        md: 9,
                                        lg: 9,
                                        xl: 9,
                                    }}
                                >
                                    Breast Cancer Trials 
                                </Grid2>
                        </Grid2>
                        <Grid2 container>
                                <Grid2
                                    size={{
                                        xs: 12,
                                        sm: 4,
                                        md: 3,
                                        lg: 3,
                                        xl: 3,
                                    }}
                                >
                                    <strong>Site Name </strong>
                                </Grid2>
                                <Grid2
                                    size={{
                                        xs: 12,
                                        sm: 8,
                                        md: 9,
                                        lg: 9,
                                        xl: 9,
                                    }}
                                >
                                    {institution?.name}
                                </Grid2>
                        </Grid2>
                    </TrialInfo>
                    <Paragraph>
                        We, the trial team at {institution?.name}, invite you to take part in a clinical trial called PROSPECTIVE. You have been invited to take part because you: 
                    </Paragraph>
                    <ul>
                        <li>
                            <Paragraph>
                                Are 50 years of age or older  
                            </Paragraph>
                        </li>
                        <li>
                            <Paragraph>
                                Have early breast cancer (breast cancer that is only in the breast and may or may not have spread to lymph nodes in the breast or armpit). 
                            </Paragraph>
                        </li>
                    </ul>
                    <Paragraph>
                        The breast cancer seems to be in a small area in the breast based on standard imaging using a mammogram with or without an ultrasound. Around 1400 people will take part in this trial. They will be from hospitals around Australia and several other countries. 
                    </Paragraph>
                    <Paragraph>
                        Please click the following link to view the full Main Consent Participant Information and Consent Form. Please read this information and feel free to ask any questions. You can also talk to someone you trust, like a family member, mob, friend, or your doctor. You can take some time to make up your mind. You get to decide if this trial is right for you.  
                    </Paragraph>
                    
                    {
                        consentForm?.picfVersion && <Box style={{ textAlign: 'center', marginTop: '0.5rem' }}>
                            <PicfButton
                                color="primary"
                                variant="contained"
                                //@ts-expect-error
                                component="a"
                                href={picfsDownloadLink}
                                target="_blank" 
                                rel="noopener" 
                            >
                                {consentForm?.picfVersion}
                            </PicfButton>
                        </Box>
                    }

                    <BoldParagraph>
                        Consent to take part in this trial 
                    </BoldParagraph>
                    <ChecklistContainer>
                        
                        <Checklist>
                            <ChecklistIcon>
                                <FontAwesomeIcon fixedWidth icon={faCheckCircle} color="primary" />
                            </ChecklistIcon>
                            <Paragraph>
                                I freely agree to take part in this trial. 
                            </Paragraph>
                        </Checklist>
                        
                        <Checklist>
                            <ChecklistIcon>
                                <FontAwesomeIcon fixedWidth icon={faCheckCircle} color="primary" />
                            </ChecklistIcon>
                            <Paragraph>
                                I understand that I can stop taking part in the trial at any time. 
                            </Paragraph>
                        </Checklist>
                        
                        <Checklist>
                            <ChecklistIcon>
                                <FontAwesomeIcon fixedWidth icon={faCheckCircle} color="primary" />
                            </ChecklistIcon>
                            <Paragraph>
                                I have read, or have had read to me in a language that I understand, the information provided about this trial and understand what is involved, including the use of my personal information. 
                            </Paragraph>
                        </Checklist>

                        <Checklist>
                            <ChecklistIcon>
                                <FontAwesomeIcon fixedWidth icon={faCheckCircle} color="primary" />
                            </ChecklistIcon>
                            <Paragraph>
                                I have had the opportunity to consider the information, ask questions and am satisfied with the answers I received.  
                            </Paragraph>
                        </Checklist>

                        <Checklist>
                            <ChecklistIcon>
                                <FontAwesomeIcon fixedWidth icon={faCheckCircle} color="primary" />
                            </ChecklistIcon>
                            <Paragraph>
                                I give permission for my medical records to be accessed for the purposes of this trial. 
                            </Paragraph>
                        </Checklist>

                        <Checklist>
                            <ChecklistIcon>
                                <FontAwesomeIcon fixedWidth icon={faCheckCircle} color="primary" />
                            </ChecklistIcon>
                            <Paragraph>
                                I understand that I may be asked to attend follow-up visits to allow collection of personal and health information about my health. Alternatively, the investigator/sponsor will request my permission to access my medical records for collection of follow-up personal and health information for research and analysis.
                            </Paragraph>
                        </Checklist>

                        <Checklist>
                            <ChecklistIcon>
                                <FontAwesomeIcon fixedWidth icon={faCheckCircle} color="primary" />
                            </ChecklistIcon>
                            <Paragraph>
                                I agree to the storage and use of tissue and blood samples and images taken from me for use as described in the relevant section of the Participant Information Sheet. 
                            </Paragraph>
                        </Checklist>

                        <Checklist>
                            <ChecklistIcon>
                                <FontAwesomeIcon fixedWidth icon={faCheckCircle} color="primary" />
                            </ChecklistIcon>
                            <Paragraph>
                                I understand that BCT may in future request specific details about me from this institution (such as name, sex, date of birth, address) for use in data linkage. I understand that requests for data linkage will only occur after specific approvals are obtained, BCT will only give my identifiable information to the data custodian(s) for data linkage, and the data custodian(s) will de-identify my information before giving it to researchers. 
                            </Paragraph>
                        </Checklist>

                        <Checklist>
                            <ChecklistIcon>
                                <FontAwesomeIcon fixedWidth icon={faCheckCircle} color="primary" />
                            </ChecklistIcon>
                            <Paragraph>
                                I understand that I will be given a signed copy of this document to keep. 
                            </Paragraph>
                        </Checklist>
                    </ChecklistContainer>
                    <MainConsentEConsentForm />
                </StyledPaper>
            </StyledWrapper>
        </>
    )
}

const MainConsentFormFields: React.FunctionComponent = () => {
    const { data: institution } = React.useContext(InstitutionContext);
    const { data: investigatorLookup, loading: investigatorsLoading } = useInvestigatorLookup('investigator', institution?.code);

    const investigators = React.useMemo(() => [investigatorLookup], [investigatorLookup]);

    const { state: { value: consentTypeId } } = useScopedField<Dtos.ConsentTypeEnum>('consentTypeId', { value: true}, false, false);

    const disabled = consentTypeId !== Dtos.ConsentTypeEnum.Standard

    return (
        <>
            <GroupedField
                propertyPath="patientSignedConsent"
                component={ToggleButtonGroup}
                disabled={disabled}
            />

            <CrfCondition
                mode={ConditionMode.Enable}
                condition={patientSigned}
                subscription={{ value: true }}
            >
                <PicfVersionField />

                <GroupedField
                    propertyPath="datePatientSignedConsent"
                    component={DatePicker}
                    disabled={disabled}
                />
                <GroupedField
                    propertyPath="participantAgreedToProvideBloodSample"
                    component={ToggleButtonGroup}
                    disabled={disabled}
                />
                <GroupedField
                    propertyPath="participantAgreedToCompleteQuestionnaires"
                    component={ToggleButtonGroup}
                    disabled={disabled}
                />
                <GroupedField
                    propertyPath="investigatorSignedConsent"
                    component={ToggleButtonGroup}
                    disabled={disabled}
                />
                <CrfCondition
                    mode={ConditionMode.Enable}
                    condition={investigatorSigned}
                    subscription={{ value: true }}
                >
                    <GroupedField
                        propertyPath="dateInvestigatorSignedConsent"
                        component={DatePicker}
                        disabled={disabled}
                    />
                    <FormLookupsContext.Provider value={investigators}>
                    {
                        investigatorsLoading ? 
                            <LinearProgress color='primary' /> : 
                            <GroupedFields>
                                <GroupedField 
                                    propertyPath="investigator"
                                    component={Select}
                                    disabled={disabled}
                                />
                                <CrfCondition
                                    mode={ConditionMode.Show}
                                    condition={investigatorOther}
                                    subscription={{ value: true }}
                                >
                                    <GroupedField
                                        propertyPath="investigatorSpecify"
                                        component={Text}
                                        disabled={disabled}
                                    />
                                </CrfCondition>
                            </GroupedFields>
                    }    
                    </FormLookupsContext.Provider>
                </CrfCondition>
            </CrfCondition>
        </>
    )
}

const consentTypeLookup: Dtos.ILookup[] = [
    {
        propertyName: 'consentTypeId',
        items:[
            {
                id: Dtos.ConsentTypeEnum.Remote,
                value: 'eConsent'
            },
            {
                id: Dtos.ConsentTypeEnum.Standard,
                value: 'Standard'
            }
        ]
    }
];

const MainConsentForm: React.FunctionComponent<IMainConsentProps> = () => {
    const { data: masterGroup } = React.useContext(MasterGroupContext);
    const { data: collaboratingGroup } = React.useContext(CollaboratingGroupContext);
    const { data: institution } = React.useContext(InstitutionContext);
    const { data: patient } = React.useContext(PatientContext);

    const {
        permissions: [
            canUpdateCrf
        ],
        loading: permissionsLoading
    } = usePermissionsByIds(
        [
            Permission.OpmsCrfUpdate
        ], 
        masterGroup?.id, 
        collaboratingGroup?.id, 
        institution?.id, 
        patient?.id
    );

    const { data: form } = React.useContext(FormContext);

    const hasChanged = React.useMemo(() => {
        return form?.enteredDate !== form?.modifiedDate;
    }, [form]);

    const isEConsent = React.useMemo(() => {
        return (form as Dtos.MainConsent)?.consentTypeId === Dtos.ConsentTypeEnum.Remote
    }, [form]);

    if (permissionsLoading) {
        return (
            <RouteLoading />
        );
    }

    return (
        <PageLayout
            breadcrumbs={<FormBreadcrumbs />}
        >
            <CrfForm
                validateOn={ValidateOn.onChange}
                readOnly={!canUpdateCrf}
                debug={AppSettings?.debug}
                errorMode={isEConsent ? 
                    FormErrorDisplayMode.AlwaysHideErrors :
                    hasChanged ? 
                        FormErrorDisplayMode.AlwaysShowAllErrors : 
                        FormErrorDisplayMode.AutoShowAllErrors
                }
            >
                <Typography sx={{px: 2, py: 3}}>
                    Main Consent must be completed prior to registration. Tissue Banking and Image Consent, and Services Australia Consent (AUS sites only) must be completed prior to allocation.
                </Typography>
                <FormLookupsContext.Provider value={consentTypeLookup}>
                    <GroupedField
                        propertyPath="consentTypeId"
                        component={ToggleButtonGroup}
                        LabelProps={{
                            label: <>Consent Type</>
                        }}
                        disabled
                    />
                </FormLookupsContext.Provider>

                <EConsentNote />

                <MainConsentFormFields />
            </CrfForm>
        </PageLayout>
    );
}

const MainConsent: React.FunctionComponent<IMainConsentProps> = () => {
    const { userType } = React.useContext(EConsentContext);

    return (
        <>
            {
                userType ? 
                    <MainConsentEConsent /> :
                    <MainConsentForm />
            }
        </>
    );
}

/*
 * ----------------------------------------------------------------------------------
 * Default Export
 * ----------------------------------------------------------------------------------
 */

export default MainConsent;
